import { Injectable, inject } from '@angular/core';
import { IntlDurationPipe } from '@app/shared/pipes/intl-duration.pipe';
import { TimeReportSessionTypeEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import moment from 'moment';
import { getWorkTimeChartDurationField, isWorkTimeChartSeries } from './work-time-chart-durations';

@Injectable()
export class TimeReportCalculatorService {
  private readonly intlDurationPipe = inject(IntlDurationPipe);

  public sumAllSessionsTime(
    filteredReports: WorkTimeReport[][],
    type: TimeReportSessionTypeEnum = TimeReportSessionTypeEnum.DURATION
  ): string {
    const summedTime = moment.duration();

    filteredReports.forEach((session: WorkTimeReport[]) => {
      summedTime.add(this.calculateDurationInSession(session, type));
    });

    return summedTime.toISOString();
  }

  public calculateDurationInSession(
    reports: WorkTimeReport[],
    type: TimeReportSessionTypeEnum = TimeReportSessionTypeEnum.DURATION
  ): string {
    const summedDuration = moment.duration();

    reports.forEach(report => {
      switch (type) {
        case TimeReportSessionTypeEnum.DURATION:
          summedDuration.add(report.duration);
          break;
        case TimeReportSessionTypeEnum.BANK_MINUS:
          break;
        default:
          if (isWorkTimeChartSeries(type)) {
            summedDuration.add(getWorkTimeChartDurationField(report, type));
            break;
          }
          summedDuration.add(report.duration);
      }
    });

    return summedDuration.toISOString();
  }

  public sumTimeForDatesAndHoursType(
    allReports: WorkTimeReport[][],
    dates: Date[],
    hoursType: TimeReportSessionTypeEnum,
    dateComparison: 'day' | 'month' = 'day'
  ): number[] {
    const results: number[] = [];

    dates.forEach(date => {
      const filteredReports = this.filterReportsByDate(allReports, date, dateComparison);
      const summedDuration = this.sumDurationByType(filteredReports, hoursType);
      results.push(+this.intlDurationPipe.transform(summedDuration.toISOString(), true));
    });

    return results;
  }

  private filterReportsByDate(
    allReports: WorkTimeReport[][],
    date: Date,
    comparison: 'day' | 'month'
  ): WorkTimeReport[] {
    return allReports.flat().filter(report => moment(date).isSame(report.date, comparison));
  }

  private sumDurationByType(reports: WorkTimeReport[], hoursType: TimeReportSessionTypeEnum): moment.Duration {
    const summedDuration = moment.duration();

    reports.forEach(report => {
      if (!isWorkTimeChartSeries(hoursType)) {
        return;
      }
      summedDuration.add(getWorkTimeChartDurationField(report, hoursType));
    });

    return summedDuration;
  }

  public parseTimeToHours(isoTime: string): number {
    return +this.intlDurationPipe.transform(isoTime, true);
  }

  public calculateOvertime(totalTime: string, userCount: number, standardWorkHours: number): number {
    const hours = this.parseTimeToHours(totalTime);
    return Math.max(0, hours - standardWorkHours * userCount);
  }
}
